import type { ElementNodeDto } from '@xshop/shared';

// What a link is called when it shows a shopper only a picture with no words: the picture's own description when it has
// one, else where the link goes, in the store's language. A link with no name is read out as "link" and nothing else,
// and Lighthouse marks the page down for it.

/** The languages a store is served in. */
type Language = 'en' | 'fr' | 'ar';

/** Where a shop's own links go, said in each language. */
const PLACES: Record<Language, Record<'home' | 'all' | 'collections' | 'collection' | 'product' | 'cart' | 'page' | 'other', string>> = {
  en: { home: 'Home', all: 'Shop all products', collections: 'All collections', collection: 'View collection', product: 'View product', cart: 'Cart', page: 'Open page', other: 'Open link' },
  fr: { home: 'Accueil', all: 'Voir tous les produits', collections: 'Toutes les collections', collection: 'Voir la collection', product: 'Voir le produit', cart: 'Panier', page: 'Ouvrir la page', other: 'Ouvrir le lien' },
  ar: { home: 'الرئيسية', all: 'تسوق جميع المنتجات', collections: 'جميع المجموعات', collection: 'عرض المجموعة', product: 'عرض المنتج', cart: 'السلة', page: 'فتح الصفحة', other: 'فتح الرابط' }
};

// Names where a link goes, by the shop route its address is.
export function placeName(href: string, language: string): string {
  const words = PLACES[(language in PLACES ? language : 'en') as Language];
  const path = href.replace(/^https?:\/\/[^/]+/, '').split(/[?#]/)[0] || '/';
  if (path === '/') return words.home;
  if (/^\/collections\/all\/?$/.test(path)) return words.all;
  if (/^\/collections\/?$/.test(path)) return words.collections;
  if (/^\/collections\//.test(path)) return words.collection;
  if (/^\/products\//.test(path)) return words.product;
  if (/^\/cart\/?$/.test(path)) return words.cart;
  return /^\/(pages|policies)\//.test(path) ? words.page : words.other;
}

// Reads the description of the first picture inside a node, or an empty string when none has one.
export function pictureDescription(node: ElementNodeDto): string {
  const alt = node.type === 'image' && typeof node.props.alt === 'string' ? node.props.alt.trim() : '';
  if (alt) return alt;
  for (const child of node.children) {
    const found = pictureDescription(child);
    if (found) return found;
  }
  return '';
}
